import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { findWhere } from "underscore";

import {
  PeopleWithInfo,
  TenantWithPeopleInfo,
} from "../../../api/api-hooks/cloud/useCloud";
import useQueriedRecords from "../../../api/api-hooks/records/useQueriedRecords";
import AppTablePagination from "../../../components/AppTable/AppTablePagination";
import FeedItemDrawer from "../../../components/Drawers/FeedItemDrawer";
import useClouds from "../../../api/api-hooks/cloud/useClouds";
import AppEmptyState from "../../../elements/AppEmptyState";
import { FeedEvent } from "../../../types/GlobalInterfaces";
import FeedItem from "../../../components/Feed/FeedItem";
import Constants from "../../../utils/Constants";

interface Props {
  cloud: TenantWithPeopleInfo | undefined;
}

const baseFilters = [
  { Field: "Type", Method: "not-equals", Value: "Security" },
  { Field: "Type", Method: "not-equals", Value: "Backup Restore" },
];

const getDefaultQuery = () => ({
  Fields: ["*"],
  Filters: [
    {
      Group: true,
      Filters: baseFilters,
    },
  ],
  OrderBy: {
    Field: "Created",
    Direction: "DESC",
  },
  Pagination: {
    Count: 25,
    Page: 1,
  },
});

const CloudFeed = ({ cloud }: Props) => {
  const { cloudID } = useParams<{ cloudID: string }>();
  const [query, setQuery] = useState(getDefaultQuery());
  const { clouds } = useClouds();
  const [selectedFeedItem, setSelectedFeedItem] = useState<FeedEvent | null>(
    null
  );
  const { records, pagination, recordsLoading } = useQueriedRecords({
    moduleAPIName: "Feed",
    query: query,
    cloudID: cloudID,
  });

  const mappedClouds: any = useRef({});

  const usersMap = useMemo(() => {
    const map: { [key: string]: PeopleWithInfo } = {};

    if (!cloud || !Array.isArray(cloud?.People)) return map;

    cloud.People.forEach((p) => (map[p?.PersonID] = p));

    return map;
  }, [cloud?.People]);

  const getCloudName = (cloudID: string) => {
    if (mappedClouds.current?.[cloudID]) {
      return mappedClouds.current[cloudID];
    } else {
      const foundCloud: any = findWhere(clouds ?? {}, { TenantID: cloudID });

      mappedClouds.current[cloudID] = foundCloud?.TenantName ?? "Deleted Cloud";

      return mappedClouds.current[cloudID];
    }
  };

  return (
    <>
      {Array.isArray(records) && records.length > 0 ? (
        <div>
          <div className="px-3 pt-2">
            {Array.isArray(records) &&
              records?.map((r) => {
                return (
                  <FeedItem
                    key={r.id}
                    record={r}
                    getUserName={(userID) => usersMap[userID || ""]?.Name}
                    getCloudName={getCloudName}
                    onOpen={() => setSelectedFeedItem(r)}
                    userLink={`/clouds/${Constants.SUP_CLOUD_ID}/supportal/users/${r.UserID}`}
                  />
                );
              })}
          </div>
          <AppTablePagination
            records={records}
            pagination={pagination}
            query={query}
            setQuery={setQuery}
            error={null}
            loading={recordsLoading}
          />
        </div>
      ) : (
        <AppEmptyState
          title="No Feed Items"
          description="There are not feed items to display in this cloud."
          className="tw:mt-5"
        />
      )}
      <FeedItemDrawer
        open={!!selectedFeedItem}
        onClose={() => setSelectedFeedItem(null)}
        feedRecord={selectedFeedItem}
        userName={usersMap[selectedFeedItem?.UserID || ""]?.Name}
      />
    </>
  );
};

export default CloudFeed;
